// pages/Payouts.jsx
import { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase";
import {
  collection,
  onSnapshot,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import {
  Search,
  Layers,
  CheckCircle2,
  Clock,
  AlertTriangle,
} from "lucide-react";
import PayoutDrawer from "../../components/PayoutDrawer";

const sym = (c) =>
  c === "USD"
    ? "$"
    : c === "EUR"
    ? "€"
    : c === "GBP"
    ? "£"
    : c === "INR"
    ? "₹"
    : c === "LKR"
    ? "Rs"
    : c || "$";
const ts = (v) => v?.toDate?.()?.toLocaleString?.() || "—";

function Row({ p, onOpen }) {
  const status = p.received
    ? "Received"
    : p.paid
    ? "Paid (confirm)"
    : "Awaiting";
  const chip = p.received
    ? "bg-emerald-400 text-black"
    : p.paid
    ? "bg-yellow-400 text-black"
    : "bg-slate-700 text-white";

  return (
    <button
      onClick={onOpen}
      className="text-left p-4 rounded-2xl bg-slate-800 ring-1 ring-slate-700 hover:ring-slate-600 transition"
    >
      <div className="flex items-center justify-between">
        <div className="text-xl font-bold">
          {sym(p.currency)}
          {Number(p.amount || 0).toFixed(2)}
        </div>
        <span className={`px-2 py-0.5 rounded text-xs font-bold ${chip}`}>
          {status}
        </span>
      </div>
      <div className="mt-1 text-xs text-slate-400">
        Ticket: <b>{p.ticketId?.slice(0, 8)}…</b> · Card:{" "}
        <b>{p.cardId?.slice(0, 8)}…</b>
      </div>
      <div className="mt-2 text-xs text-slate-500">
        Paid: {p.paid ? ts(p.paidAt) : "—"} · Received:{" "}
        {p.received ? ts(p.receivedAt) : "—"}
      </div>
      {p.dispute?.isOpen && (
        <div className="mt-2 inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-amber-500/20 text-amber-300">
          <AlertTriangle size={14} /> Dispute open
        </div>
      )}
    </button>
  );
}

export default function Payouts({ user }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("confirm"); // confirm | awaiting | received | disputes
  const [drawerId, setDrawerId] = useState("");

  useEffect(() => {
    if (!user?.uid) return;
    const qy = query(
      collection(db, "payouts"),
      where("userUid", "==", user.uid),
      orderBy("paidAt", "desc")
    );
    const un = onSnapshot(qy, (s) => {
      const arr = [];
      s.forEach((d) => arr.push({ id: d.id, ...d.data() }));
      setRows(arr);
    });
    return () => un();
  }, [user?.uid]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? rows.filter(
          (r) =>
            r.ticketId?.toLowerCase().includes(q) ||
            r.cardId?.toLowerCase().includes(q)
        )
      : rows;

    switch (tab) {
      case "awaiting":
        return base.filter((r) => !r.paid);
      case "confirm":
        return base.filter((r) => r.paid && !r.received);
      case "received":
        return base.filter((r) => r.received);
      case "disputes":
        return base.filter((r) => r.dispute?.isOpen);
      default:
        return base;
    }
  }, [rows, search, tab]);

  return (
    <div className="max-w-5xl mx-auto mt-8 md:mt-16 p-4">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h1 className="text-2xl font-bold">Payouts</h1>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className="pl-9 pr-3 py-2 rounded-xl bg-slate-800 text-white ring-1 ring-slate-700 w-72"
            placeholder="Search ticket or card…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* tabs */}
      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={() => setTab("confirm")}
          className={`px-3 py-1.5 rounded-full text-sm ${
            tab === "confirm"
              ? "bg-emerald-400 text-black"
              : "bg-slate-700 text-white"
          }`}
        >
          Paid (confirm)
        </button>
        <button
          onClick={() => setTab("awaiting")}
          className={`px-3 py-1.5 rounded-full text-sm ${
            tab === "awaiting"
              ? "bg-slate-400 text-black"
              : "bg-slate-700 text-white"
          }`}
        >
          Awaiting payment
        </button>
        <button
          onClick={() => setTab("received")}
          className={`px-3 py-1.5 rounded-full text-sm ${
            tab === "received"
              ? "bg-blue-400 text-black"
              : "bg-slate-700 text-white"
          }`}
        >
          Received
        </button>
        <button
          onClick={() => setTab("disputes")}
          className={`px-3 py-1.5 rounded-full text-sm ${
            tab === "disputes"
              ? "bg-amber-400 text-black"
              : "bg-slate-700 text-white"
          }`}
        >
          Disputes
        </button>
      </div>

      {filtered.length ? (
        <div className="grid md:grid-cols-2 gap-4">
          {filtered.map((p) => (
            <Row key={p.id} p={p} onOpen={() => setDrawerId(p.id)} />
          ))}
        </div>
      ) : (
        <div className="p-4 rounded-xl bg-slate-800 text-slate-300">
          Nothing here.
        </div>
      )}

      {drawerId && (
        <PayoutDrawer
          open={!!drawerId}
          payoutId={drawerId}
          user={user}
          onClose={() => setDrawerId("")}
        />
      )}
    </div>
  );
}
